import type { AgentBuilderWebhookTriggerType } from "@app/components/agent_builder/AgentBuilderFormContext";
import { RecentWebhookRequests } from "@app/components/agent_builder/triggers/RecentWebhookRequests";
import { TriggerPodSelector } from "@app/components/agent_builder/triggers/TriggerPodSelector";
import type { TriggerViewsSheetFormValues } from "@app/components/agent_builder/triggers/triggerViewsSheetFormSchema";
import { WebhookEditionFilters } from "@app/components/agent_builder/triggers/webhook/WebhookEditionFilters";
import type { TriggerExecutionMode } from "@app/types/assistant/triggers";
import type { WebhookSourceViewType } from "@app/types/triggers/webhooks";
import { WEBHOOK_PRESETS } from "@app/types/triggers/webhooks";
import type {
  BaseWebhookPreset,
  WebhookEvent,
} from "@app/types/triggers/webhooks_source_preset";
import type { LightWorkspaceType } from "@app/types/user";
import {
  AlertCircle,
  Button,
  Checkbox,
  ContentMessage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
  SliderToggle,
  TextArea,
} from "@dust-tt/sparkle";
// biome-ignore lint/correctness/noUnusedImports: ignored using `--suppress`
import React, { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

interface WebhookEditionNameInputProps {
  isEditor: boolean;
}

function WebhookEditionNameInput({ isEditor }: WebhookEditionNameInputProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name: "webhook.name" });

  return (
    <div className="flex-grow space-y-1">
      <Label htmlFor="webhook-name">
        {t("builder.webhookNameLabel", "Name")}
      </Label>
      <Input
        id="webhook-name"
        placeholder={t("builder.webhookNamePlaceholder", "Enter trigger name")}
        disabled={!isEditor}
        {...field}
        isError={!!error}
        message={error?.message}
        messageStatus="error"
      />
    </div>
  );
}

interface WebhookEditionStatusToggleProps {
  isEditor: boolean;
}

function WebhookEditionStatusToggle({
  isEditor,
}: WebhookEditionStatusToggleProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field: { value: status, onChange: setStatus },
  } = useController({ control, name: "webhook.status" });

  const isEnabled = status === "enabled";

  return (
    <div className="space-y-1">
      <Label>{t("builder.webhookStatusLabel", "Status")}</Label>
      <div className="flex flex-row items-center gap-2">
        <span className="w-16">
          {isEnabled
            ? t("builder.webhookStatusEnabled", "Enabled")
            : t("builder.webhookStatusDisabled", "Disabled")}
        </span>
        <SliderToggle
          disabled={!isEditor}
          selected={isEnabled}
          onClick={() => setStatus(isEnabled ? "disabled" : "enabled")}
        />
      </div>
    </div>
  );
}

interface WebhookEditionExecutionLimitProps {
  executionMode: TriggerExecutionMode;
}

function WebhookEditionExecutionLimit({
  executionMode,
}: WebhookEditionExecutionLimitProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field: { value: executionLimit },
  } = useController({
    control,
    name: "webhook.executionPerDayLimitOverride",
  });

  return (
    <div className="flex flex-col space-y-1">
      <Label htmlFor="execution-limit">
        {t("builder.webhookRateLimitsLabel", "Rate limits")}
      </Label>
      <p>
        {t(
          "builder.webhookRateLimitsWindow",
          "Limits are set on a 24-hour window."
        )}{" "}
      </p>
      <ContentMessage
        variant="info"
        size="lg"
        icon={AlertCircle}
        title={t(
          "builder.webhookRateLimitsTitle",
          "Up to {{limit}} requests per day",
          { limit: executionLimit }
        )}
      >
        {t(
          "builder.webhookRateLimitsBody",
          "This trigger can send a limited number of messages per day. This prevents a single trigger from using up your workspace's message fair use quota. This trigger is currently running on your workspace's {{quota}} quota.",
          {
            quota:
              executionMode === "fair_use" ? "fair use" : "programmatic usage",
          }
        )}
        <br /> ({t("builder.webhookRateLimitsLearnMore", "Learn more")}
        {/* TODO(rebrand): restore the "Learn more" link (rate limiting) once Dasis docs exist */}
        )
      </ContentMessage>
    </div>
  );
}

interface WebhookEditionEventSelectorProps {
  isEditor: boolean;
  selectedPreset: BaseWebhookPreset | null;
  availableEvents: WebhookEvent[];
}

function WebhookEditionEventSelector({
  isEditor,
  selectedPreset,
  availableEvents,
}: WebhookEditionEventSelectorProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field: { value: selectedEvent, onChange: setSelectedEvent },
    fieldState: { error },
  } = useController({ control, name: "webhook.event" });

  if (!selectedPreset || availableEvents.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-1">
      <Label htmlFor="webhook-event">
        {t("builder.webhookListenForLabel", "Listen for")}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t(
          "builder.webhookEventSelectorDescription",
          "External event that will trigger a run of this agent."
        )}
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id="webhook-event"
            variant="outline"
            isSelect
            className="w-fit"
            disabled={!isEditor}
            label={
              availableEvents.find((e) => e.value === selectedEvent)?.name ??
              t("builder.webhookSelectEventFallback", "Select event")
            }
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel
            label={t("builder.webhookEventSelectorDropdownLabel", "Select")}
          />
          {availableEvents.map((event) => (
            <DropdownMenuItem
              key={event.value}
              label={event.name}
              disabled={!isEditor}
              onClick={() => setSelectedEvent(event.value)}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {error && <p className="text-sm text-warning">{error.message}</p>}
    </div>
  );
}

interface WebhookEditionIncludePayloadProps {
  isEditor: boolean;
}

function WebhookEditionIncludePayload({
  isEditor,
}: WebhookEditionIncludePayloadProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const {
    field: { value: includePayload, onChange: setIncludePayload },
  } = useController({ control, name: "webhook.includePayload" });

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={includePayload}
        onClick={() => setIncludePayload(!includePayload)}
        disabled={!isEditor}
      />
      <Label>
        {t("builder.webhookIncludePayloadLabel", "Include webhook payload")}
      </Label>
    </div>
  );
}

interface WebhookEditionMessageInputProps {
  isEditor: boolean;
}

function WebhookEditionMessageInput({
  isEditor,
}: WebhookEditionMessageInputProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const { field } = useController({ control, name: "webhook.customPrompt" });

  return (
    <div className="space-y-1">
      <Label htmlFor="webhook-prompt">
        {t("builder.webhookMessageLabel", "Message (optional)")}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t(
          "builder.webhookMessageInputDescription",
          "Message for the agent when the trigger runs."
        )}
      </p>
      <TextArea
        id="webhook-prompt"
        minRows={4}
        disabled={!isEditor}
        {...field}
      />
    </div>
  );
}

interface WebhookEditionPodSelectorProps {
  isEditor: boolean;
  owner: LightWorkspaceType;
}

function WebhookEditionPodSelector({
  isEditor,
  owner,
}: WebhookEditionPodSelectorProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<TriggerViewsSheetFormValues>();
  const { field } = useController({ control, name: "webhook.spaceId" });

  return (
    <div className="space-y-1">
      <Label>
        {t(
          "builder.webhookPodSelectorLabel",
          "Where to create this conversation? (optional)"
        )}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t(
          "builder.webhookPodSelectorDescription",
          "Run this trigger's conversation inside a Pod instead."
        )}
      </p>
      <TriggerPodSelector
        owner={owner}
        value={field.value}
        onChange={field.onChange}
        disabled={!isEditor}
      />
    </div>
  );
}

interface WebhookEditionSheetContentProps {
  owner: LightWorkspaceType;
  trigger: AgentBuilderWebhookTriggerType | null;
  agentConfigurationId: string | null;
  webhookSourceView: WebhookSourceViewType | null;
  isEditor: boolean;
}

export function WebhookEditionSheetContent({
  owner,
  trigger,
  agentConfigurationId,
  webhookSourceView,
  isEditor,
}: WebhookEditionSheetContentProps) {
  const { t } = useTranslation();
  const selectedPreset = useMemo((): BaseWebhookPreset | null => {
    if (!webhookSourceView || webhookSourceView.provider === null) {
      return null;
    }
    return WEBHOOK_PRESETS[webhookSourceView.provider];
  }, [webhookSourceView]);

  const availableEvents = useMemo(() => {
    if (!selectedPreset || !webhookSourceView) {
      return [];
    }

    return selectedPreset.events.filter((event) =>
      webhookSourceView.subscribedEvents.includes(event.value)
    );
  }, [selectedPreset, webhookSourceView]);

  return (
    <>
      {trigger && !isEditor && (
        <ContentMessage variant="info">
          <Trans
            i18nKey="builder.webhookCannotEditManagedBy"
            defaults="You cannot edit this trigger. It is managed by <0>{{editorName}}</0>."
            values={{
              editorName:
                trigger.editorName ??
                t("builder.webhookAnotherUser", "another user"),
            }}
            components={[<span key="0" className="font-semibold" />]}
          />
        </ContentMessage>
      )}
      <div className="space-y-8">
        <div className="flex flex-row items-center justify-between gap-4">
          <WebhookEditionNameInput isEditor={isEditor} />
          <WebhookEditionStatusToggle isEditor={isEditor} />
        </div>

        <WebhookEditionEventSelector
          isEditor={isEditor}
          selectedPreset={selectedPreset}
          availableEvents={availableEvents}
        />

        <WebhookEditionFilters
          isEditor={isEditor}
          webhookSourceView={webhookSourceView}
          selectedPreset={selectedPreset}
          availableEvents={availableEvents}
          workspace={owner}
        />

        <Separator />

        <div className="space-y-4">
          <WebhookEditionMessageInput isEditor={isEditor} />
          <WebhookEditionIncludePayload isEditor={isEditor} />
        </div>

        <Separator />

        <WebhookEditionExecutionLimit
          executionMode={trigger?.executionMode ?? "fair_use"}
        />

        <Separator />

        <WebhookEditionPodSelector isEditor={isEditor} owner={owner} />

        {trigger && (
          <div className="space-y-1">
            <RecentWebhookRequests
              owner={owner}
              agentConfigurationId={agentConfigurationId}
              trigger={trigger}
            />
          </div>
        )}
      </div>
    </>
  );
}
